// @flow strict

import React from "react";
import { withRouter } from "react-router-dom";
import type { RouterHistory } from "react-router-dom";
import { imgSrc, emptyObject } from "~common/utils";
import { AVATAR_PLAYER, PATHS, playersMap } from "~robin-hood/utils";
import type { Player } from "~robin-hood/utils";
import type { RegisteredStore, Agent, Equipo } from "~robin-hood/redux/store";
import { getAgentPlayers } from "~robin-hood/redux/store";
import { InFrame } from "~robin-hood/Frame";
import { simpleDots } from "cli-spinners";
import classnames from "~common/classnames";
import styles from "./PruebaInicial.module.css";

const AVATAR_BG_PLAYER = playersMap([
  imgSrc("/images/robin-hood/prueba-inicial/ciudad1.png"),
  imgSrc("/images/robin-hood/prueba-inicial/ciudad2.png"),
  imgSrc("/images/robin-hood/prueba-inicial/ciudad3.png"),
  imgSrc("/images/robin-hood/prueba-inicial/ciudad4.png"),
  imgSrc("/images/robin-hood/prueba-inicial/ciudad5.png"),
  imgSrc("/images/robin-hood/prueba-inicial/ciudad6.png"),
]);

const INICIAL_SECRET_PLAYER = playersMap([
  "1142221531",
  "2412245511",
  "3224311133",
  "3224334325",
  "3511422443",
  "3515252433",
]);

type Props = {
  agent: Agent,
  equipo: Equipo,
  player: Player,
  store: RegisteredStore,
  updateLocalStore: (store: RegisteredStore) => void,
};

function valueFromLocal(store: RegisteredStore, player: Player) {
  const inicial =
    store.local?.inicial ||
    emptyObject<{ [Player]: { value: string, correct: boolean } }>();
  return inicial[player] ? inicial[player].value : "";
}

function correctFromLocal(store: RegisteredStore, player: Player) {
  const inicial =
    store.local?.inicial ||
    emptyObject<{ [Player]: { value: string, correct: boolean } }>();
  return inicial[player] ? inicial[player].correct : false;
}

function localStoreWithInicial(
  store: RegisteredStore,
  player: Player,
  value: string,
  correct?: boolean
): RegisteredStore {
  const inicialInStore: { [Player]: { value: string, correct: boolean } } =
    store.local?.inicial || {};
  inicialInStore[player] = { value, correct: !!correct };
  return {
    ...store,
    local: {
      ...store.local,
      inicial: inicialInStore,
    },
  };
}

class PruebaInicialClass extends React.PureComponent<{
  ...Props,
  history: RouterHistory,
}> {
  componentDidMount() {
    const { player, store, history } = this.props;
    const isCorrect = correctFromLocal(store, player);
    if (isCorrect) {
      history.push(PATHS.CONSOLA(player));
    }
  }

  _onCheck = () => {
    const { player, store, history, updateLocalStore } = this.props;
    const value = valueFromLocal(store, player);
    if (value === INICIAL_SECRET_PLAYER.get(player)) {
      updateLocalStore(localStoreWithInicial(store, player, value, true));
      history.push(PATHS.CONSOLA(player));
    }
  };

  render(): React$Node {
    const {
      store,
      player,
      equipo,
      agent,
      history,
      updateLocalStore,
    } = this.props;
    const backgroundStyle = {
      backgroundImage: `url(${AVATAR_BG_PLAYER.get(player) || ""})`,
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
    };
    const players = getAgentPlayers(equipo, agent);
    return (
      <InFrame style={backgroundStyle} noToolbar>
        <div className={styles.pruebaInicial}>
          {players.length > 1 && (
            <div
              className={styles.goBack}
              onClick={() => {
                // TODO If knows some secret, relogins there.
                history.push(PATHS.AGENTES());
              }}
            >
              <span className="fa fa-long-arrow-alt-left"></span>
            </div>
          )}
          <div className={styles.wrapper}>
            <div className={styles.wrapperContent}>
              <AgenteAvatar player={player} />
              <Solucion
                player={player}
                history={history}
                store={store}
                updateLocalStore={updateLocalStore}
              />
            </div>
          </div>
        </div>
      </InFrame>
    );
  }
}

export const PruebaInicial: React$ComponentType<Props> = withRouter(
  PruebaInicialClass
);

type AgenteAvatarProps = {
  player: Player,
};

function AgenteAvatar({ player }: AgenteAvatarProps) {
  return (
    <div className={styles.avatar}>
      <div className={styles.avatarWrapper}>
        <img alt="Avatar" src={AVATAR_PLAYER.get(player)} />
      </div>
    </div>
  );
}

type SolucionProps = {
  player: Player,
  history: RouterHistory,
  store: RegisteredStore,
  updateLocalStore: (RegisteredStore) => void,
};

const SPINNER = simpleDots;
const RESET = 2000;

function next(idx: ?number): number {
  return ((idx ?? 0) + 1) % SPINNER.frames.length;
}

class Solucion extends React.PureComponent<
  SolucionProps,
  { checking?: number, checked: boolean, tick: number }
> {
  _mounted: boolean;
  _timeout: ?TimeoutID;
  state = {
    checking: undefined,
    checked: false,
    tick: 0,
  };

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = undefined;
    }
  }

  _doCheck = () => {
    const { player, store, history, updateLocalStore } = this.props;
    const value = valueFromLocal(store, player);
    if (value === INICIAL_SECRET_PLAYER.get(player)) {
      updateLocalStore(localStoreWithInicial(store, player, value, true));
      history.push(PATHS.CONSOLA(player));
    }
  };

  _tick = () => {
    if (!this._mounted) {
      clearTimeout(this._timeout);
      this._timeout = undefined;
      return;
    }
    this.setState(
      (state) => ({
        tick: state.tick + 1,
        checking: next(state.checking),
      }),
      () => {
        if (!this._mounted || this._timeout) return;
        const TICKS = SPINNER.frames.length * 3;
        if (this.state.tick === TICKS - 1) {
          // Check early so result is ready
          this._doCheck();
        }
        if (this.state.tick < TICKS) {
          this._timeout = setTimeout(() => {
            this._timeout = undefined;
            this._tick();
          }, SPINNER.interval);
        } else {
          // Finished
          this.setState(
            {
              tick: 0,
              checking: undefined,
              checked: true,
            },
            () => {
              this._timeout = setTimeout(() => {
                this.setState({ checked: false });
                this._timeout = undefined;
              }, RESET);
            }
          );
        }
      }
    );
  };

  _doChange = (e) => {
    const value = e.target.value;
    const { player, store, updateLocalStore } = this.props;
    updateLocalStore(localStoreWithInicial(store, player, value));
  };

  _doStartCheck = () => {
    const { checking, checked } = this.state;
    if (checking || this._timeout) return;
    if (checked) {
      this.setState({ checked: false });
    } else {
      this._tick();
    }
  };

  render(): React$Node {
    const { store, player } = this.props;
    const value = valueFromLocal(store, player);
    const { checking, checked } = this.state;
    const content: string =
      checking != null ? SPINNER.frames[checking] : !checked ? "LOG IN" : "";

    return (
      <div className={styles.solucionWrapper}>
        <form
          className={styles.solucion}
          onSubmit={(e) => {
            e.preventDefault();
            this._doStartCheck();
          }}
        >
          <input
            type="text"
            autoFocus
            defaultValue={value}
            onChange={this._doChange}
          />
          <span
            className={classnames(
              styles.solucionCheck,
              checking != null
                ? styles.solucionChecking
                : checked && styles.solucionIncorrect
            )}
            onClick={this._doStartCheck}
          >
            {content}
          </span>
        </form>
      </div>
    );
  }
}
